import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Save, X, DollarSign, CheckCircle, AlertCircle, Calculator } from 'lucide-react';
import { Commission, Agent, Application } from '@/api/entities';

export default function AddCommissionModal({ isOpen, onClose, onCommissionAdded }) {
  const [formData, setFormData] = useState({
    agent_id: '',
    application_id: '',
    student_name: '',
    program: '',
    base_amount: 25000,
    commission_rate: 15,
    adjustment_amount: 0,
    status: 'pending'
  });
  const [agents, setAgents] = useState([]);
  const [applications, setApplications] = useState([]);
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [calculatedCommission, setCalculatedCommission] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error'

  useEffect(() => {
    if (isOpen) {
      fetchData();
    }
  }, [isOpen]);

  useEffect(() => {
    // Filter applications by selected agent
    if (formData.agent_id) {
      const agentApps = applications.filter(app => app.agent_id === formData.agent_id);
      setFilteredApplications(agentApps);
    } else {
      setFilteredApplications([]);
    }
  }, [formData.agent_id, applications]);

  useEffect(() => {
    // Calculate commission when relevant fields change
    const commission = (formData.base_amount * formData.commission_rate / 100) + formData.adjustment_amount;
    setCalculatedCommission(commission);
  }, [formData.base_amount, formData.commission_rate, formData.adjustment_amount]);

  const fetchData = async () => {
    try {
      const [agentsData, applicationsData] = await Promise.all([
        Agent.list(),
        Application.list()
      ]);
      setAgents(agentsData.filter(agent => agent.status === 'active'));
      setApplications(applicationsData.filter(app => app.status === 'enrolled')); // Only enrolled students are eligible
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAgentChange = (agentId) => {
    const selectedAgent = agents.find(agent => agent.id === agentId);
    setFormData(prev => ({
      ...prev,
      agent_id: agentId,
      commission_rate: selectedAgent?.commission_rate || 15,
      application_id: '', // Reset application selection
      student_name: '',
      program: ''
    }));
  };

  const handleApplicationChange = (applicationId) => {
    const selectedApp = filteredApplications.find(app => app.id === applicationId);
    if (selectedApp) {
      setFormData(prev => ({
        ...prev,
        application_id: applicationId,
        student_name: selectedApp.student_name,
        program: selectedApp.program_applied
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.agent_id || !formData.student_name || !formData.program) return;

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const commissionData = {
        ...formData,
        commission_amount: calculatedCommission,
        payment_date: formData.status === 'paid' ? new Date().toISOString() : null
      };

      await Commission.create(commissionData);
      setSubmitStatus('success');
      
      // Reset form
      setFormData({
        agent_id: '',
        application_id: '',
        student_name: '',
        program: '',
        base_amount: 25000,
        commission_rate: 15,
        adjustment_amount: 0,
        status: 'pending'
      });
      
      // Notify parent component
      if (onCommissionAdded) {
        onCommissionAdded();
      }
      
    } catch (error) {
      console.error('Error creating commission:', error);
      setSubmitStatus('error');
    }
    
    setIsSubmitting(false);
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setSubmitStatus(null);
      setFormData({
        agent_id: '',
        application_id: '',
        student_name: '',
        program: '',
        base_amount: 25000,
        commission_rate: 15,
        adjustment_amount: 0,
        status: 'pending'
      });
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-green-600" />
            Add New Commission
          </DialogTitle>
        </DialogHeader>
        
        {submitStatus === 'success' ? (
          <div className="text-center py-8">
            <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-4" />
            <h3 className="font-bold text-lg">Commission Added Successfully!</h3>
            <p className="text-muted-foreground">The commission record has been created and is now being tracked.</p>
          </div>
        ) : submitStatus === 'error' ? (
          <div className="text-center py-8">
            <AlertCircle className="w-12 h-12 mx-auto text-red-500 mb-4" />
            <h3 className="font-bold text-lg">Creation Failed</h3>
            <p className="text-muted-foreground">There was an error creating the commission. Please try again.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Agent Selection */}
            <div className="space-y-2">
              <Label>Select Agent *</Label>
              <Select value={formData.agent_id} onValueChange={handleAgentChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose an agent" />
                </SelectTrigger>
                <SelectContent>
                  {agents.map(agent => (
                    <SelectItem key={agent.id} value={agent.id}>
                      {agent.company_name} ({agent.commission_rate}%)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Application Selection */}
            {formData.agent_id && (
              <div className="space-y-2">
                <Label>Select Application (Optional)</Label>
                <Select value={formData.application_id} onValueChange={handleApplicationChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose from enrolled students" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">Manual Entry</SelectItem>
                    {filteredApplications.map(app => (
                      <SelectItem key={app.id} value={app.id}>
                        {app.student_name} - {app.program_applied}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Student Details */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Student Name *</Label>
                <Input
                  value={formData.student_name}
                  onChange={(e) => handleInputChange('student_name', e.target.value)}
                  placeholder="Enter student name"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Program *</Label>
                <Input
                  value={formData.program}
                  onChange={(e) => handleInputChange('program', e.target.value)}
                  placeholder="Enter program name"
                  required
                />
              </div>
            </div>

            {/* Commission Calculation */}
            <div className="bg-gray-50 border rounded-lg p-4 space-y-4">
              <div className="flex items-center gap-2 mb-2">
                <Calculator className="w-4 h-4 text-indigo-600" />
                <h4 className="font-semibold">Commission Calculation</h4>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Base Amount ($)</Label>
                  <Input
                    type="number"
                    value={formData.base_amount}
                    onChange={(e) => handleInputChange('base_amount', parseFloat(e.target.value) || 0)}
                    placeholder="25000"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Commission Rate (%)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.commission_rate}
                    onChange={(e) => handleInputChange('commission_rate', parseFloat(e.target.value) || 0)}
                    placeholder="15"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Adjustment Amount ($)</Label>
                  <Input
                    type="number"
                    value={formData.adjustment_amount}
                    onChange={(e) => handleInputChange('adjustment_amount', parseFloat(e.target.value) || 0)}
                    placeholder="0"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={formData.status} onValueChange={(value) => handleInputChange('status', value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="paid">Paid</SelectItem>
                      <SelectItem value="disputed">Disputed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Calculation Result */}
              <div className="bg-white border rounded-lg p-3">
                <div className="flex justify-between items-center">
                  <span className="font-medium">Total Commission:</span>
                  <span className="text-xl font-bold text-green-600">
                    ${calculatedCommission.toLocaleString()}
                  </span>
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  Base: ${formData.base_amount.toLocaleString()} × {formData.commission_rate}% + ${formData.adjustment_amount}
                </div>
              </div>
            </div>
          </form>
        )}
        
        <DialogFooter>
          {submitStatus ? (
            <Button onClick={handleClose} className="w-full">Close</Button>
          ) : (
            <>
              <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button 
                onClick={handleSubmit} 
                disabled={!formData.agent_id || !formData.student_name || !formData.program || isSubmitting}
                className="flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                {isSubmitting ? 'Creating...' : 'Add Commission'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}